import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Check, ExternalLink, Github, ShoppingCart } from 'lucide-react';
import { supabase } from '../supabaseClient';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProduct = React.useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*, categories(name)')
        .eq('id', id)
        .single();

      if (error) throw error;
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handlePurchase = async () => {
    if (!product) return;

    // Razorpay integration
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: product.price * 100, // Amount in paise
      currency: 'INR',
      name: 'Script Saint',
      description: product.name,
      image: '/logo.png',
      handler: async function (response) {
        // Save order to Supabase
        const { error } = await supabase.from('orders').insert([
          {
            product_id: product.id,
            customer_email: 'customer@example.com', // Should be from user input
            customer_name: 'Customer Name', // Should be from user input
            razorpay_payment_id: response.razorpay_payment_id,
            amount: product.price,
            status: 'completed',
            github_invite_sent: false
          }
        ]);

        if (!error) {
          alert('Payment successful! You will receive GitHub repo access via email.');
        }
      },
      prefill: {
        name: 'Your Name',
        email: 'your@email.com'
      },
      theme: {
        color: '#00d9ff'
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <button onClick={() => navigate('/shop')} className="gradient-button">
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-gray-400 hover:text-white transition mb-8"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div>
            <div className="glass-card p-4 rounded-lg mb-4">
              <div className="aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Github size={64} className="text-gray-600" />
                  </div>
                )}
              </div>
            </div>
            {product.demo_url && (
              <a
                href={product.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card p-4 rounded-lg flex items-center justify-center space-x-2 text-cyan-400 hover:text-cyan-300 transition"
              >
                <ExternalLink size={20} />
                <span>View Live Demo</span>
              </a>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="mb-6">
              {product.categories && (
                <span className="text-cyan-400 text-sm font-semibold mb-2 inline-block">
                  {product.categories.name}
                </span>
              )}
              <h1 className="text-4xl font-bold font-poppins mb-4">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center space-x-1 text-yellow-400">
                  <Star size={20} fill="currentColor" />
                  <span className="font-semibold">{product.rating || '5.0'}</span>
                </div>
                <span className="text-gray-400">•</span>
                <span className="text-gray-400">Production Ready</span>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">{product.description}</p>
            </div>

            {/* Price & Purchase */}
            <div className="glass-card p-6 rounded-lg mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400">Price</span>
                <span className="text-4xl font-bold gradient-text">₹{product.price}</span>
              </div>
              <button
                onClick={handlePurchase}
                className="w-full gradient-button py-4 flex items-center justify-center space-x-2 text-lg"
              >
                <ShoppingCart size={20} />
                <span>Buy Now</span>
              </button>
              <p className="text-gray-400 text-sm text-center mt-4">
                ✓ Lifetime access • ✓ Full source code • ✓ Documentation included
              </p>
            </div>

            {/* Tech Stack */}
            {product.tech_stack && product.tech_stack.length > 0 && (
              <div className="glass-card p-6 rounded-lg mb-6">
                <h3 className="font-bold text-lg mb-3">Tech Stack</h3>
                <div className="flex flex-wrap gap-2">
                  {product.tech_stack.map((tech, index) => (
                    <span key={index} className="px-3 py-2 bg-gray-800/50 rounded-lg text-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="glass-card p-6 rounded-lg">
                <h3 className="font-bold text-lg mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Check className="text-green-400 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;